<header class="content-header">
  <h4 class="vision-h4">Create Tasks</h4>
  <div class="project-context" *ngIf="data.project_id && includesTagsContainer">
    <span test-id="project-context">{{"Project" | titlecase }}: {{ includesTagsContainer.label }}</span>
  </div>
</header>

<div mat-dialog-content>
  <form [formGroup]="formGroup" class="create-group-form">
    <div class="task-criteria" *ngIf="!data.project_id && currentUser">
      <label aria-label="Select Project">
        Select Project
      </label>
      <div class="mat-form-field">
        <project-picker #projectPicker
          test-id="project-picker"
          [filter]="getProjectsFilter()"
          (valueChange)="selectProject($event)"
          [validators]="[requiredValidator, projectHasDataValidator]"
        >
          <mat-error *ngIf="projectPicker.touched && projectPicker.errors?.required">
            Project is required.
          </mat-error>
          <mat-error *ngIf="projectPicker.touched && projectPicker.errors?.empty">
            {{ projectPicker.errors.empty }}
          </mat-error>
        </project-picker>
      </div>
    </div>

    <div class="task-criteria">
      <label aria-label="Task Type">
        Task Type
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="task-type" formControlName="taskType">
          <mat-option *ngFor="let taskType of taskTypes" [value]="taskType.value">{{ taskType.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.taskType.touched && formGroup.controls.taskType.hasError('required')">{{
"Task Type is required." }}</mat-error>
      </mat-form-field>
    </div>

    <!-- Read protocols only display for "Reader" task type -->
    <div class="task-criteria" *ngIf="formGroup.controls.taskType.value === 'R' && protocolOptions">
      <label aria-label="Read Protocol">
        Read Protocol
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="read-protocol" formControlName="readProtocol" placeholder="Select Read Protocol">
          <mat-option *ngFor="let protocol of protocolOptions" [value]="protocol.value">{{ protocol.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.readProtocol.hasError('required')">Read protocol is required.</mat-error>
      </mat-form-field>
      <div class="read-protocol-warning" *ngIf="protocolOptions.length === 0">
        <ng-container *ngIf="getProjectLabel(formGroup.controls.project.value) as projectLabel; else noProjectForProtocols">
          <mat-icon class="xs orange">warning</mat-icon><i>{{"No read protocols were found for project labeled '{{0}}'. See documentation for details to add a protocol for read tasks." | interpolate:projectLabel }}</i>
        </ng-container>
        <ng-template #noProjectForProtocols>
          <ng-container *ngIf="projectWasTouched()">
            <mat-icon class="xs orange">warning</mat-icon><i>No read protocols were found. See documentation for details to add a protocol for read tasks.</i>
          </ng-container>
        </ng-template>
      </div>
    </div>

    <div class="task-criteria">
      <label aria-label="Container">
        Container
      </label>
      <mat-form-field appearance="outline">
        <mat-select test-id="fw-object-select" formControlName="container"
          placeholder="Select Container">
          <mat-option *ngFor="let container of containerTypes" [value]="container.value">{{ container.label }}</mat-option>
        </mat-select>
        <mat-error *ngIf="formGroup.controls.container.hasError('required')">{{ 'createTaskDialog.error.containerRequired' |
          translate }}</mat-error>
      </mat-form-field>
    </div>

    <div class="full-width" *ngIf="includesTagsContainer">
      <label aria-label="Container Filter">
        Container Filter
      </label>
      <div>
        <mat-label>Contains tag(s)</mat-label>
        <fw-tags
          test-id="container-includes-tags"
          [useAsInlineField]="true"
          formControlName="containerIncludesTags"
          [container]="includesTagsContainer"
          [autoSave]="false">
        </fw-tags>
      </div>
      <div>
        <mat-label>Does NOT contain tag(s) </mat-label>
        <fw-tags test-id="container-excludes-tags" [useAsInlineField]="true" formControlName="containerExcludesTags"
          [container]="excludesTagsContainer" [autoSave]="false">
        </fw-tags>
      </div>
    </div>

    <div class="container-count full-width" *ngIf="shouldFetchContainerCount$ | async">
      <ng-container *ngIf="!loadingMatchingContainers; else loadingContainerCount">
        <strong class="blue" style="font-size: 16px;">{{ matchingContainerCount }}</strong>
        <span>{{ selectedContainerLabel }} found</span>
      </ng-container>
      <ng-template #loadingContainerCount>
        <mat-spinner diameter="16"></mat-spinner>
        <span>... searching for {{ selectedContainerLabel | lowercase }}</span>
      </ng-template>
    </div>

    <div class="task-criteria">
      <div class="flex-label">
        <label aria-label="Assignee">
          Assignee
        </label>
        <mat-icon fwTooltip="This list displays user with the correct permissions to perform Read Tasks in the system." class="xs">info</mat-icon>
      </div>
      <div class="mat-form-field">
        <app-typeahead
          #assigneeTypeahead
          test-id="user-typeahead"
          class="solid-background full-width"
          [disabled]="formGroup.get('assignee').disabled"
          placeholder="Assignee"
          [options]="userOptions"
          [validators]="[requiredAssigneeValidator, noAssigneesValidator]"
          (valueChange)="updateAssignee($event.toString())">
        </app-typeahead>
      </div>
    </div>

    <div class="task-criteria">
      <label aria-label="Date Due">
        Date Due
      </label>
      <mat-form-field appearance="outline" class="date-field flex-field">
        <input test-id="due-date-input" formControlName="dueDate" matInput [matDatepicker]="datepicker"
          placeholder="Select Date" autocomplete="off">
        <mat-datepicker-toggle [for]="datepicker" test-id="due-date-picker-toggle"></mat-datepicker-toggle>
        <mat-datepicker #datepicker></mat-datepicker>
        <mat-error *ngIf="formGroup.controls.dueDate.hasError('required')">Date Due is required.</mat-error>
        <mat-error *ngIf="formGroup.controls.dueDate.hasError('dateIsPast')">Date Due must be a future date.</mat-error>
      </mat-form-field>
    </div>

    <div class="full-width" *ngIf="taskTagsContainer">
      <label>Task Tags</label>
      <fw-tags
        [allowCustomTags]="true"
        [useAsInlineField]="true"
        formControlName="tags"
        [container]="taskTagsContainer"
        [autoSave]="false">
      </fw-tags>
    </div>
  </form>
</div>

<div class="action-buttons" mat-dialog-actions align="end">
  <div class="footer-create-more">
    <mat-checkbox test-id="create-more-checkbox" [(ngModel)]="createMore"></mat-checkbox>
    <span>Create More Tasks</span>
  </div>
  <button test-id="close-button" mat-stroked-button (click)="cancel()">Cancel</button>
  <button test-id="create-button" mat-flat-button color="primary" (click)="submit()"
    [disabled]="formGroup.invalid">
    Create
  </button>
</div>
